/**
 * Hook for GoCardless mandate migration
 * Provides sync, import, and manual association capabilities
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mandateMigrationApi } from '../services/mandate.service';
import type {
  SyncFromGoCardlessOptions,
  SyncResult,
  CSVImportRow,
  ImportResult,
  AssociateRequest,
  UnassociatedMandate,
  GoCardlessMandateInfo,
  PaymentMandate,
} from '../types/mandate.types';

// Query keys
export const mandateMigrationKeys = {
  all: ['mandateMigration'] as const,
  unassociated: () => [...mandateMigrationKeys.all, 'unassociated'] as const,
  lookup: (id: string) => [...mandateMigrationKeys.all, 'lookup', id] as const,
};

/**
 * Hook to sync mandates from GoCardless
 */
export function useSyncMandates() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, SyncFromGoCardlessOptions | undefined>({
    mutationFn: mandateMigrationApi.sync,
    onSuccess: () => {
      // Invalidate unassociated mandates and mandate list
      queryClient.invalidateQueries({ queryKey: mandateMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to import mandates from CSV/JSON
 */
export function useImportMandates() {
  const queryClient = useQueryClient();

  return useMutation<ImportResult, Error, CSVImportRow[]>({
    mutationFn: mandateMigrationApi.import,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mandateMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to manually associate a mandate to a client
 */
export function useAssociateMandate() {
  const queryClient = useQueryClient();

  return useMutation<PaymentMandate, Error, AssociateRequest>({
    mutationFn: mandateMigrationApi.associate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mandateMigrationKeys.unassociated() });
      queryClient.invalidateQueries({ queryKey: ['mandates'] });
    },
  });
}

/**
 * Hook to get unassociated mandates
 */
export function useUnassociatedMandates() {
  return useQuery<UnassociatedMandate[], Error>({
    queryKey: mandateMigrationKeys.unassociated(),
    queryFn: mandateMigrationApi.getUnassociated,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to lookup a mandate by GoCardless ID
 */
export function useLookupMandate(gocardlessMandateId: string | null) {
  return useQuery<GoCardlessMandateInfo | null, Error>({
    queryKey: mandateMigrationKeys.lookup(gocardlessMandateId || ''),
    queryFn: () => mandateMigrationApi.lookup(gocardlessMandateId!),
    enabled: !!gocardlessMandateId,
    staleTime: 60000, // 1 minute
  });
}
